import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import toast from 'react-hot-toast';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const { LuGraduationCap, LuEye, LuEyeOff } = LuIcons;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('idle');
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const ensureProfileExists = async (userId, userEmail) => {
    try {
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();

      if (!existing) {
        // Trigger didn't fire or failed — manually create a minimal profile
        await supabase.from('profiles').insert([{
          id: userId,
          email: userEmail.toLowerCase().trim(),
          role: 'teacher',
          is_default_password: true,
        }]);
      }
    } catch (err) {
      console.error('ensureProfileExists error:', err);
    }
  };

  const claimPendingInvite = async (userId, userEmail) => {
    try {
      const normalizedEmail = userEmail.toLowerCase().trim();

      const { data: invite, error: inviteErr } = await supabase
        .from('pending_invites')
        .select('*')
        .eq('email', normalizedEmail)
        .eq('claimed', false)
        .maybeSingle();

      if (inviteErr) {
        console.error('Invite fetch error:', inviteErr);
        return;
      }

      if (!invite) return;

      // Check if profile already has full data (beyond the trigger-created stub)
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, first_name')
        .eq('id', userId)
        .maybeSingle();

      if (!existingProfile || !existingProfile.first_name) {
        // Upsert with full invite data — sets is_default_password=true
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert([{
            id: userId,
            email: normalizedEmail,
            first_name: invite.first_name || '',
            last_name: invite.last_name || '',
            mobile_number: invite.mobile_number || '',
            teacher_nickname: invite.teacher_nickname || '',
            role: invite.role || 'teacher',
            is_default_password: true,
            created_by: invite.created_by,
          }], { onConflict: 'id' });

        if (upsertError) {
          console.error('Profile upsert error:', upsertError);
        }
      }

      // Mark the invite as claimed via SECURITY DEFINER RPC
      const { data: claimed, error: rpcError } = await supabase
        .rpc('claim_pending_invite', { p_email: normalizedEmail });

      if (rpcError) {
        console.error('RPC claim_pending_invite error:', rpcError);
      } else {
        console.log('Invite claim result:', claimed, 'for', normalizedEmail);
      }
    } catch (err) {
      console.error('Error in claimPendingInvite:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setIsLoading(true);
    const normalizedEmail = email.toLowerCase().trim();

    try {
      // ── Step 1: Try normal sign-in first ──────────────────────────────────
      setStep('signing_in');
      try {
        const data = await signIn(normalizedEmail, password);
        if (data?.user) {
          await ensureProfileExists(data.user.id, data.user.email);
          await claimPendingInvite(data.user.id, data.user.email);
          toast.success('Welcome back!');
          navigate('/');
          return;
        }
      } catch (signInErr) {
        const isInvalidCreds =
          signInErr?.message?.toLowerCase().includes('invalid login') ||
          signInErr?.message?.toLowerCase().includes('invalid credentials') ||
          signInErr?.message?.toLowerCase().includes('email not confirmed') ||
          signInErr?.status === 400;

        if (!isInvalidCreds) {
          toast.error(signInErr?.message || 'Sign-in failed. Please try again.');
          setIsLoading(false);
          setStep('idle');
          return;
        }
        // Invalid credentials — fall through to check pending invite
      }

      // ── Step 2: Check for an unclaimed pending invite ─────────────────────
      setStep('checking');
      const { data: invite, error: inviteErr } = await supabase
        .from('pending_invites')
        .select('id, email, temp_password, first_name, last_name, role')
        .eq('email', normalizedEmail)
        .eq('claimed', false)
        .maybeSingle();

      if (inviteErr) console.error('Invite lookup error:', inviteErr);

      if (!invite) {
        toast.error('Invalid email or password. Please check your credentials.');
        setIsLoading(false);
        setStep('idle');
        return;
      }

      // ── Step 3: Validate temp password ────────────────────────────────────
      if (invite.temp_password !== password) {
        toast.error('Invalid password. Use the temporary password provided by your administrator.');
        setIsLoading(false);
        setStep('idle');
        return;
      }

      // ── Step 4: Create auth account for first-time teacher ────────────────
      setStep('signing_up');
      const { data: signupData, error: signupErr } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
      });

      if (signupErr) {
        if (signupErr.message?.includes('already registered') || signupErr.status === 422) {
          toast.error(
            'An account with this email already exists but the password is incorrect. ' +
            'Contact your administrator to reset your password.',
            { duration: 8000 }
          );
        } else {
          toast.error(`Account setup failed: ${signupErr.message}`);
        }
        setIsLoading(false);
        setStep('idle');
        return;
      }

      // ── Step 5: Sign in with newly created account ────────────────────────
      if (signupData?.user) {
        setStep('signing_in');

        // The on_auth_user_created trigger creates a minimal profile automatically.
        // We then upsert with the full invite data (including is_default_password=true).
        if (signupData.session) {
          await ensureProfileExists(signupData.user.id, signupData.user.email);
          await claimPendingInvite(signupData.user.id, signupData.user.email);
          toast.success(`Welcome, ${invite.first_name || 'Teacher'}! Your account is now active.`, { duration: 5000 });
          navigate('/');
          return;
        }

        try {
          const signInData = await signIn(normalizedEmail, password);
          if (signInData?.user) {
            await ensureProfileExists(signInData.user.id, signInData.user.email);
            await claimPendingInvite(signInData.user.id, signInData.user.email);
            toast.success(`Welcome, ${invite.first_name || 'Teacher'}! Your account is now active.`, { duration: 5000 });
            navigate('/');
            return;
          }
        } catch (secondSignInErr) {
          toast.error(
            '⚠️ Your account was created but email confirmation is required. ' +
            'Ask your administrator to disable "Email Confirmations" in Supabase → Authentication → Settings.',
            { duration: 10000 }
          );
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
      setStep('idle');
    }
  };

  const getButtonLabel = () => {
    if (!isLoading) return 'Sign In';
    switch (step) {
      case 'checking': return 'Checking credentials...';
      case 'signing_up': return 'Setting up account...';
      case 'signing_in': return 'Signing in...';
      default: return 'Please wait...';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
        <div className="flex justify-center mb-8">
          <div className="bg-primary-600 text-white p-4 rounded-2xl shadow-lg shadow-primary-500/30">
            <SafeIcon icon={LuGraduationCap} size={40} />
          </div>
        </div>
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Happynestling Admin</h1>
          <p className="text-slate-500 mt-2">Sign in to manage your tuition center</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@happynestling.com"
            required
            disabled={isLoading}
          />
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <SafeIcon icon={showPassword ? LuEyeOff : LuEye} size={16} />
            </button>
          </div>
          <Button
            type="submit"
            className="w-full py-2.5 text-base mt-2"
            disabled={isLoading || !email || !password}
          >
            {getButtonLabel()}
          </Button>
        </form>
        <div className="mt-6 space-y-3">
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
            <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-2">
              For Administrators
            </p>
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Use your registered email and password to sign in.
            </p>
          </div>
          <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
            <p className="text-[10px] font-black uppercase text-amber-600 tracking-widest mb-2">
              For New Staff
            </p>
            <p className="text-[10px] text-amber-700 leading-relaxed">
              Use the <strong>email</strong> and <strong>temporary password</strong> provided by your
              administrator. Your account will be activated automatically on first sign-in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}